<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="outreach-contact-top-reaction">
    <BaseOutreachContactStat
      v-if="!hasTopReaction"
      :tooltip="
        getLocaleText('outreach_contact_top_reaction.no_top_reaction_tooltip')
      "
    >
      <div class="outreach-contact-top-reaction__no-value">
        {{ topReactionText }}
      </div>
    </BaseOutreachContactStat>

    <BaseButtonDropdown
      v-else
      interaction="hover"
      new-design
      new-dropdown
    >
      <template #btn="{ listeners }">
        <div
          class="outreach-contact-top-reaction__container"
          v-on="listeners"
        >
          <BaseOutreachContactStat>
            <div class="outreach-contact-top-reaction__container-emoji">
              {{ topReactionEmoji }}
            </div>

            {{ topReactionText }}
          </BaseOutreachContactStat>
        </div>
      </template>
      <div class="outreach-contact-top-reaction__stats">
        <div class="outreach-contact-top-reaction__stats-title">
          {{ getLocaleText("outreach_contact_top_reaction.top_reaction") }}
        </div>

        <div
          class="outreach-contact-top-reaction__stats-description"
          v-html="reactionsDescription"
        />

        <div class="outreach-contact-top-reaction__stats-row">
          <div
            v-for="reaction in reactions"
            :key="reaction.id"
            class="outreach-contact-top-reaction__stat"
          >
            <div class="outreach-contact-top-reaction__stat-emoji">
              {{ reaction.emoji }}
            </div>

            <div class="outreach-contact-top-reaction__stat-label">
              {{ reaction.text }}
            </div>

            <div class="outreach-contact-top-reaction__stat-value">
              {{ reaction.value }}
            </div>
          </div>
        </div>
      </div>
    </BaseButtonDropdown>
  </div>
</template>

<script setup>
import camelCase from "lodash/camelCase";
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButtonDropdown } from "shared/components/base";
import BaseOutreachContactStat from "shared/components/Outreach/OutreachContact/BaseOutreachContactStat.vue";
import { prettyNumber } from "shared/helpers/number";

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const topReaction = computed(() => props.contact.dominantFacebookReaction);

const hasTopReaction = computed(() => Boolean(topReaction.value));

const REACTIONS = {
  ANGRY: "angry",
  HAHA: "haha",
  LOVE: "love",
  SAD: "sad",
  WOW: "wow",
};

const REACTION_EMOJIS = {
  [REACTIONS.LOVE]: "😍",
  [REACTIONS.WOW]: "😲",
  [REACTIONS.HAHA]: "😄",
  [REACTIONS.SAD]: "😢",
  [REACTIONS.ANGRY]: "😡",
};

const topReactionEmoji = computed(() => REACTION_EMOJIS[topReaction.value]);

function getReactionText(reactionType) {
  return getLocaleText(`outreach_contact_top_reaction.${reactionType}`);
}

const topReactionText = computed(() =>
  hasTopReaction.value ? getReactionText(topReaction.value) : "--"
);

function getReactionTotal(reactionType) {
  const key = camelCase(`total_${reactionType}_reactions`);

  return props.contact[key] || 0;
}

const totalReactions = computed(() => {
  const totals = Object.values(REACTIONS).map((reactionType) =>
    getReactionTotal(reactionType)
  );

  return totals.reduce((acc, total) => acc + total, 0);
});

const totalReactionsFormatted = computed(() =>
  prettyNumber(totalReactions.value, { notation: "compact" })
);

const reactionPercentages = computed(() => {
  const percentages = {};

  Object.values(REACTIONS).forEach((reactionType) => {
    const total = getReactionTotal(reactionType);
    let percentage = 0;

    if (totalReactions.value && total) {
      percentage = Math.round((total / totalReactions.value) * 100);
    }

    percentages[reactionType] = `${percentage}%`;
  });

  return percentages;
});

const reactionsDescription = computed(() =>
  getLocaleText("outreach_contact_top_reaction.description", {
    total: totalReactionsFormatted.value,
    reaction: topReactionText.value,
    percentage: reactionPercentages.value[topReaction.value],
  })
);

const reactions = computed(() =>
  Object.values(REACTIONS).map((reactionType) => ({
    id: reactionType,
    emoji: REACTION_EMOJIS[reactionType],
    text: getReactionText(reactionType),
    value: reactionPercentages.value[reactionType],
  }))
);
</script>

<style lang="scss" scoped>
.outreach-contact-top-reaction {
  font-weight: 500;
  line-height: 150%;

  &__no-value {
    color: var(--s-color-denim-5);
    text-decoration: none;
  }

  &__container {
    :deep(.q-chip__content) {
      gap: var(--s-spacing-sm);
    }

    &-emoji {
      font-size: var(--s-font-size-lg);
    }
  }

  &__stats {
    display: flex;
    flex-direction: column;
    gap: var(--s-spacing-lg);
    padding: var(--s-spacing-lg);
    max-width: 400px;

    &-title {
      font-weight: var(--s-font-weight-bold);
      line-height: 150%;
      color: var(--s-color-denim-9);
    }

    &-description {
      font-weight: normal;
      line-height: 150%;
      color: var(--s-color-denim-7);

      :deep(strong) {
        color: var(--s-color-denim-9);
      }
    }

    &-row {
      display: flex;
      flex: 1;
      justify-content: space-between;
      gap: 24px;
    }
  }

  &__stat {
    display: flex;
    flex-direction: column;
    align-items: center;

    &-emoji {
      font-size: 24px;
    }

    &-label {
      color: var(--s-color-denim-7);
      font-weight: 500;
      line-height: 150%;
      margin-top: 6px;
    }

    &-value {
      margin-top: 8px;
      display: flex;
      width: 55px;
      padding: 4px 16px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 8px;
      border-radius: 100px;
      border: 1px solid var(--s-color-denim-2);
      background-color: var(--s-color-denim-1);
      font-size: 12px;
      font-weight: 500;
      line-height: 150%;
    }
  }
}
</style>
